/*****************************************************
 * Packages
 ******************************************************/
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { useParams } from 'react-router-dom';
import React, { useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/*****************************************************
 * Ours
 ******************************************************/
import Input from '../Input';
import FileUpload from '../FileUpload';
import InputGroup from './InputGroup';
import Button from '../other-components/Button';
import { toastrWarningOptions } from '../Toastr/Toastr';
import { getConfig, getIsLoggedIn } from '../../redux/selectors';

/*****************************************************
 * Styles
 ******************************************************/
const Wrap = styled.div`
    label {
        border-top-left-radius: 0;
    }
    input {
        border-top-right-radius: 0;
    }
`;

const PreviewArea = styled.div`
    display: flex;
    height: 192px;
    overflow: hidden;
    position: relative;
    align-items: center;
    justify-content: center;
    border-radius: 5px 5px 0 0;
    border: 1px solid ${({ theme }) => theme.inputBorder};
    border-bottom: 0;

    img {
        width: 100%;
    }

    .remove {
        top: 0;
        right: 0;
        color: #fff;
        width: 40px;
        height: 40px;
        display: flex;
        cursor: pointer;
        position: absolute;
        align-items: center;
        justify-content: center;
        border-radius: 0 0 0 4px;
        background: rgba(0, 0, 0, 0.6);
        border-left: 1px solid ${({ theme }) => theme.inputBorder};
        border-bottom: 1px solid ${({ theme }) => theme.inputBorder};
    }
`;

function url(string) {
    const url = string.match(/url\('(.+)'\)/i);
    return url ? url[1] : string;
}

/*****************************************************
 * Component
 ******************************************************/
const ImagePreview = (props) => {
    const {
        size,
        name,
        value,
        accept,
        fileType,
        onChange,
        addButtonText = 'Add New Image',
    } = props;
    const params = useParams();
    const config = useSelector(getConfig);
    const isLoggedIn = useSelector(getIsLoggedIn);
    const [isUploading, setUploading] = useState(false);
    const { siteId = '' } = params;

    const handleChooseFile = useCallback(() => {
        setUploading(true);
    }, []);

    const handleChange = useCallback(
        (e) => onChange({ name, value: e.target.value.trim() }),
        [name, onChange]
    );
    const handleUploadFailure = useCallback((message) => {
        setUploading(false);
        toastr.warning(message, toastrWarningOptions);
    }, []);

    const handleSuccessUpload = useCallback(
        (response) => {
            setUploading(false);
            onChange({ name, value: response.url });
        },
        [name, onChange]
    );

    return (
        <Wrap>
            <PreviewArea>
                {value ? (
                    <>
                        <span
                            className="remove"
                            onClick={() =>
                                handleChange({ target: { value: '' } })
                            }
                        >
                            <FontAwesomeIcon
                                fixedWidth
                                icon={['far', 'trash-alt']}
                            />
                        </span>
                        <img src={value} alt="Preview" />
                    </>
                ) : (
                    <FileUpload
                        size={size}
                        accept={accept}
                        run={isLoggedIn}
                        fileType={fileType}
                        onChoose={handleChooseFile}
                        onError={handleUploadFailure}
                        url={`${config.apiURL}/upload?siteId=${siteId}`}
                        onSuccess={handleSuccessUpload}
                    >
                        <Button
                            border="dotted"
                            btnType="button"
                            icon={['far', 'plus']}
                        >
                            {addButtonText}{' '}
                            {isUploading && (
                                <FontAwesomeIcon
                                    spin
                                    fixedWidth
                                    icon={['far', 'spinner']}
                                />
                            )}
                        </Button>
                    </FileUpload>
                )}
            </PreviewArea>
            <InputGroup label="URL">
                <Input
                    size="sm"
                    name={name}
                    value={value}
                    onChange={handleChange}
                />
            </InputGroup>
        </Wrap>
    );
};
export default ImagePreview;
